import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search } from 'lucide-react';
import { states } from '../../data/locations/states';
import { cities } from '../../data/locations/cities';
import { getAreasForCity } from '../../data/locations/areas';
import { useLocation } from '../../modules/location/LocationContext';

export default function LocationSearchBar() {
  const navigate = useNavigate();
  const { updateLocation } = useLocation();
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [availableCities, setAvailableCities] = useState<Array<{ value: string; label: string }>>([]);
  const [availableAreas, setAvailableAreas] = useState<Array<{ value: string; label: string; pincode?: string }>>([]);

  useEffect(() => {
    if (selectedState && cities[selectedState]) {
      setAvailableCities(cities[selectedState] || []);
      setSelectedCity('');
      setSelectedArea('');
    } else {
      setAvailableCities([]);
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedCity) {
      const areas = getAreasForCity(selectedCity);
      setAvailableAreas(areas);
      setSelectedArea('');
    } else {
      setAvailableAreas([]);
    }
  }, [selectedCity]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedState || !selectedCity || !selectedArea) return;

    updateLocation({
      state: selectedState,
      city: selectedCity,
      area: selectedArea
    });

    navigate(`/locations/${selectedState}/${selectedCity}/area/${selectedArea}`);
  };

  return (
    <div className="relative max-w-3xl mx-auto">
      <form onSubmit={handleSearch} className="bg-white rounded-lg shadow-lg p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* State Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="inline-block w-4 h-4 mr-1" />
              Select State
            </label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>

          {/* City Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="inline-block w-4 h-4 mr-1" />
              Select City
            </label>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={!selectedState}
            >
              <option value="">Select City</option>
              {availableCities.map((city) => (
                <option key={city.value} value={city.value}>
                  {city.label}
                </option>
              ))}
            </select>
          </div>

          {/* Area Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <MapPin className="inline-block w-4 h-4 mr-1" />
              Select Area
            </label>
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={!selectedCity}
            >
              <option value="">Select Area</option>
              {availableAreas.map((area) => (
                <option key={area.value} value={area.value}>
                  {area.label} {area.pincode ? `(${area.pincode})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={!selectedState || !selectedCity || !selectedArea}
          className="mt-4 w-full flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Search className="w-5 h-5 mr-2" />
          Find Services
        </button>
      </form>
    </div>
  );
}